"use client";
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useSite } from "@/lib/hooks/site";
import { getSiteCampaignPreview } from "@/lib/site-campaign/fetch-config";
import { readJson, removeKey, writeJson } from "@/lib/site-campaign/storage";
import { isPathTargeted } from "@/lib/site-campaign/targeting";
import type { SiteCampaignConfig } from "@/lib/site-campaign/types";

const SiteCampaignDrawerController = dynamic(() => import("./SiteCampaignDrawerController"), { ssr: false });

const PREVIEW_PARAM = "sc_preview";
const PREVIEW_KEY = "sc:preview";

interface ContextValue {
  config: SiteCampaignConfig | null;
  preview: boolean;
}

const SiteCampaignContext = createContext<ContextValue>({ config: null, preview: false });

export const useSiteCampaign = () => useContext(SiteCampaignContext);

interface Props {
  config: SiteCampaignConfig | null;
  children: ReactNode;
}

/**
 * Şirket İndirimleri Reklam (SAB-361): yayındaki ayarları sağlar, drawer
 * denetleyicisini yalnızca drawer etkinse yükler. Panelden gelen
 * `?sc_preview=` jetonu varsa taslağı gösterir; önizleme sekme kapanana
 * kadar sürer ve hiçbir gösterim/tıklama kaydı göndermez.
 */
export default function SiteCampaignProvider({ config, children }: Props) {
  const [previewConfig, setPreviewConfig] = useState<SiteCampaignConfig | null>(null);

  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get(PREVIEW_PARAM);
    const token = fromUrl || readJson<{ token?: string }>("session", PREVIEW_KEY, {}).token;
    if (!token) return;

    let cancelled = false;
    getSiteCampaignPreview(token).then((result) => {
      if (cancelled) return;
      if (result) {
        writeJson("session", PREVIEW_KEY, { token });
        setPreviewConfig(result);
      } else {
        removeKey("session", PREVIEW_KEY);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const exitPreview = useCallback(() => {
    removeKey("session", PREVIEW_KEY);
    setPreviewConfig(null);
  }, []);

  const active = previewConfig ?? config;
  const preview = Boolean(previewConfig);

  return (
    <SiteCampaignContext.Provider value={{ config: active, preview }}>
      {children}
      {active?.drawer && active.drawer.creatives.length > 0 && (
        <SiteCampaignDrawerController key={preview ? "preview" : "live"} config={active} preview={preview} />
      )}
      {preview && <PreviewBadge config={active} onExit={exitPreview} />}
    </SiteCampaignContext.Provider>
  );
}

function PreviewBadge({ config, onExit }: { config: SiteCampaignConfig; onExit: () => void }) {
  const pathname = usePathname();
  const site = useSite();
  const routes = site?.setting?.routes;
  const yesNo = (value: boolean) => (value ? "evet" : "hayır");

  return (
    <div className="fixed left-2 top-2 z-[100000] flex max-w-[calc(100%-1rem)] items-center gap-2 rounded-lg bg-black/80 px-3 py-2 text-xs text-white shadow-lg">
      <span>
        <strong>Kampanya önizlemesi</strong> · drawer bu sayfada:{" "}
        {yesNo(isPathTargeted(pathname, config.drawer?.targeting, routes))} · buton bu sayfada:{" "}
        {yesNo(isPathTargeted(pathname, config.button?.targeting, routes))}
      </span>
      <button type="button" onClick={onExit} className="rounded bg-white/20 px-2 py-1 font-bold hover:bg-white/30">
        Çık
      </button>
    </div>
  );
}
